import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'

const SubscriptionBilling = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  // Static data
  const currentPlan = {
    price: '$49/month',
    renewDate: 'Apr 25, 2024'
  }

  const usageStats = {
    requests: { current: 12430, limit: 20000, percentage: 62.15 },
    users: { current: 12, limit: 15, percentage: 80 },
    storage: { current: 4.5, limit: 10, percentage: 45 },
    apiCalls: { amount: 124.50, percentage: 12.45 }
  }

  const billingHistory = [
    { invoice: 'Sep Invoice', date: '5/27/15', plan: 'Pro', amount: '$49.00', status: 'Paid' },
    { invoice: 'Sep Invoice', date: '5/19/12', plan: 'Pro', amount: '$49.00', status: 'Paid' },
    { invoice: 'Sep Invoice', date: '3/4/16', plan: 'Pro', amount: '$49.00', status: 'Paid' },
    { invoice: 'Sep Invoice', date: '3/4/16', plan: 'Pro', amount: '$49.00', status: 'Pending' }
  ]

  const paymentMethod = {
    cardNumber: '**** 4242',
    expiryDate: '09/26'
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const profileImageStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.primary,
    cursor: 'pointer',
    position: 'relative'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0,
    marginBottom: '24px'
  }

  const sectionStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const sectionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  }

  const sectionTitleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: 0
  }

  const upgradeButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease'
  }

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  }

  const statCardStyle = {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: colors.background.secondary
  }

  const statLabelStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.text.secondary,
    marginBottom: '8px'
  }

  const statValueStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: '12px'
  }

  const progressBarContainerStyle = {
    width: '100%',
    height: '8px',
    backgroundColor: colors.borderVariants.light,
    borderRadius: '4px',
    overflow: 'hidden'
  }

  const progressBarStyle = (percentage) => ({
    width: `${percentage}%`,
    height: '100%',
    backgroundColor: colors.primary,
    transition: 'width 0.3s ease'
  })

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const tableHeaderStyle = {
    backgroundColor: colors.background.secondary,
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const tableHeaderCellStyle = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary
  }

  const tableRowStyle = {
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const tableCellStyle = {
    padding: '12px 16px',
    fontSize: '14px',
    color: colors.text.primary
  }

  const statusBadgeStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: status === 'Paid' ? '#10B981' : '#F59E0B',
    color: 'white'
  })

  const downloadButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.primary,
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const paymentMethodCardStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: colors.background.secondary,
    borderRadius: '8px',
    marginTop: '16px'
  }

  const paymentInfoStyle = {
    fontSize: '16px',
    fontWeight: '500',
    color: colors.text.primary
  }

  const updateButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.primary,
    backgroundColor: 'white',
    border: `1px solid ${colors.primary}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  return (
    <div style={containerStyle}>
      <Sidebar />

      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Subscription & Billing"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />

        {/* Subscription Overview Section */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h2 style={sectionTitleStyle}>Subscription & Billing</h2>
            <button
              style={upgradeButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.primary
              }}
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              Upgrade Plan
            </button>
          </div>

          <div style={statsGridStyle}>
            {/* Current Plan Card */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Current Plan</div>
              <div style={statValueStyle}>{currentPlan.price}</div>
              <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                Renew on {currentPlan.renewDate}
              </div>
            </div>

            {/* Requests Card */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Requests</div>
              <div style={statValueStyle}>
                {usageStats.requests.current.toLocaleString()} / {usageStats.requests.limit.toLocaleString()}
              </div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.requests.percentage)}></div>
              </div>
            </div>

            {/* Users Card */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Users</div>
              <div style={statValueStyle}>
                {usageStats.users.current} / {usageStats.users.limit}
              </div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.users.percentage)}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing History Summary Section */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Billing History</h2>
          
          <div style={statsGridStyle}>
            {/* Requests Summary */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Requests</div>
              <div style={statValueStyle}>
                {usageStats.requests.current.toLocaleString()} / {usageStats.requests.limit.toLocaleString()}
              </div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.requests.percentage)}></div>
              </div>
            </div>

            {/* Team Members Summary */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Team Members</div>
              <div style={statValueStyle}>
                {usageStats.users.current} / {usageStats.users.limit}
              </div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.users.percentage)}></div>
              </div>
            </div>

            {/* Storage Summary */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>Storage</div>
              <div style={statValueStyle}>
                {usageStats.storage.current} GB / {usageStats.storage.limit} GB
              </div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.storage.percentage)}></div>
              </div>
            </div>

            {/* API Calls Summary */}
            <div style={statCardStyle}>
              <div style={statLabelStyle}>API Calls</div>
              <div style={statValueStyle}>${usageStats.apiCalls.amount.toFixed(2)}</div>
              <div style={progressBarContainerStyle}>
                <div style={progressBarStyle(usageStats.apiCalls.percentage)}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing History Table Section */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Billing History</h2>
          
          <table style={tableStyle}>
            <thead style={tableHeaderStyle}>
              <tr>
                <th style={tableHeaderCellStyle}>Invoice #</th>
                <th style={tableHeaderCellStyle}>Date</th>
                <th style={tableHeaderCellStyle}>Plan</th>
                <th style={tableHeaderCellStyle}>Amount</th>
                <th style={tableHeaderCellStyle}>Status</th>
                <th style={tableHeaderCellStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory.map((invoice, index) => (
                <tr key={index} style={tableRowStyle}>
                  <td style={tableCellStyle}>{invoice.invoice}</td>
                  <td style={tableCellStyle}>{invoice.date}</td>
                  <td style={tableCellStyle}>{invoice.plan}</td>
                  <td style={tableCellStyle}>{invoice.amount}</td>
                  <td style={tableCellStyle}>
                    <span style={statusBadgeStyle(invoice.status)}>
                      {invoice.status}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <button
                      style={downloadButtonStyle}
                      title="Download"
                      onMouseEnter={(e) => {
                        e.target.style.color = colors.primaryDark
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = colors.primary
                      }}
                    >
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Payment Method Section */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Payment Method</h2>
          
          <div style={paymentMethodCardStyle}>
            <div style={paymentInfoStyle}>
              {paymentMethod.cardNumber} Exp. {paymentMethod.expiryDate}
            </div>
            <button
              style={updateButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.background.secondary
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white'
              }}
            >
              Update Payment Method
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionBilling

